/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { ReactNode } from "react";

import MuiAutocomplete, {
  AutocompleteProps as MuiAutocompleteProps
} from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

import { ChevronDownIcon, colors } from "@dotnet/telematics-design-system";

import { muiOutlinedInputDefaultStylesOverrides } from "../../shared";
import Label from "../Label/Label";
import TooltipLarge from "../Tooltip/TooltipLarge";
import { TooltipProps } from "../Tooltip/types";
import {
  largeTooltipTextFieldStyleOverrides,
  muiAutocompleteComponentPropsStyleOverrides,
  muiAutocompleteStyleOverrides
} from "./styleFunctions";

type AutocompleteSize = "tiny" | "small" | "medium";

export interface AutocompleteProps {
  size: AutocompleteSize;
  labelLeft?: string | ReactNode;
  labelIcon?: string;
  labelRight?: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  errorText?: string | null;
  isRequired?: boolean;
  width?: string;
  tooltipProps?: TooltipProps;
  textFieldParams?: {};
  customStyle?: {};
  labelCustomStyle?: {};
  hideRequiredStar?: boolean;
}

const Autocomplete = ({
  size,
  labelLeft,
  labelIcon,
  labelRight,
  iconLeft,
  iconRight,
  errorText,
  isRequired,
  width,
  tooltipProps,
  textFieldParams = {},
  customStyle,
  labelCustomStyle,
  hideRequiredStar = false,
  ...props
}: AutocompleteProps &
  Omit<
    MuiAutocompleteProps<any, boolean, boolean, boolean>,
    "renderInput" | "size"
  > & { "data-testid"?: string }) => {
  const paddings = {
    tiny: "2px 12px",
    small: "8px 12px",
    medium: "12px"
  };

  const padding = paddings[size];

  const getWidth = () => {
    if (props.fullWidth) {
      return "100%";
    }

    return width || "unset";
  };

  const containerWidth = getWidth();

  const changeDisabledTextFieldStyle =
    props["data-testid"] === "rows-per-page-autocomplete" && props.disabled;

  const changedDisabledBackground =
    changeDisabledTextFieldStyle && colors.white;

  const disabledBackground = props.disabled && colors.gray100;

  return (
    <div
      css={css({
        display: "flex",
        flexDirection: "column",
        width: containerWidth,
        ...customStyle
      })}
    >
      {(labelLeft || labelRight) && (
        <Label
          titleLeft={labelLeft}
          titleRight={labelRight}
          titleTooltip={labelIcon}
          isRequired={isRequired}
          labelCustomStyle={labelCustomStyle}
          hideRequiredStar={hideRequiredStar}
        />
      )}

      <MuiAutocomplete
        {...props}
        onOpen={props.onOpen}
        options={props.options}
        autoComplete
        autoHighlight
        getOptionKey={option => option.id}
        popupIcon={
          <ChevronDownIcon
            style={{
              color: colors.gray500
            }}
          />
        }
        renderInput={(params: any) => (
          <TooltipLarge
            {...tooltipProps}
            title={tooltipProps?.title || ""}
            placement="top"
          >
            <TextField
              {...params}
              {...textFieldParams}
              error={Boolean(errorText)}
              onKeyDown={e => {
                if (e.key === "Backspace") {
                  e.stopPropagation();
                }
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  spellCheck: "false",
                  ...(iconLeft && {
                    startAdornment: (
                      <InputAdornment position="start">
                        {iconLeft}
                      </InputAdornment>
                    )
                  }),
                  ...(iconRight && {
                    endAdornment: (
                      <InputAdornment position="start">
                        {iconRight}
                        {params.InputProps.endAdornment}
                      </InputAdornment>
                    )
                  })
                }
              }}
              sx={[
                largeTooltipTextFieldStyleOverrides(colors),
                {
                  "& .MuiInputBase-root": {
                    backgroundColor:
                      changedDisabledBackground ||
                      disabledBackground ||
                      undefined
                  }
                }
              ]}
            />
          </TooltipLarge>
        )}
        slotProps={{
          paper: {
            sx: muiAutocompleteComponentPropsStyleOverrides(colors, props)
          }
        }}
        sx={[
          muiAutocompleteStyleOverrides(colors, props, padding),
          muiOutlinedInputDefaultStylesOverrides(colors)
        ]}
      />
    </div>
  );
};

export default Autocomplete;
